import React, { useEffect, useRef, useState } from 'react';

/**
 * Reveal: A lightweight on-scroll reveal wrapper.
 * Props:
 * - children: ReactNode
 * - as: string | React.ElementType (wrapper element type)
 * - from: 'up' | 'down' | 'left' | 'right' | 'fade' (default: 'up')
 * - delay: number (ms) optional
 * - className: string
 */
const Reveal = ({
  children,
  as: Tag = 'div',
  from = 'up',
  delay = 0,
  className = '',
  ...rest
}) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Respect reduced motion
    const prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      setVisible(true);
      return;
    }

    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Defer reveal to allow stagger
            const timer = setTimeout(() => setVisible(true), delay);
            observer.unobserve(entry.target);
            return () => clearTimeout(timer);
          }
        });
      },
      { threshold: 0.15 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [delay]);

  const initialTransform = {
    up: 'translate-y-4',
    down: '-translate-y-4',
    left: 'translate-x-4',
    right: '-translate-x-4',
    fade: 'scale-[0.995]'
  }[from] || 'translate-y-4';

  return (
    <Tag
      ref={ref}
      className={`transition-all duration-700 ease-out will-change-transform ${
        visible ? 'opacity-100 translate-x-0 translate-y-0 scale-100' : `opacity-0 ${initialTransform}`
      } ${className}`}
      {...rest}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
